/**
 * Job Detail Page
 * Full job details fetched from /api/v2/jobs/:id/
 * Reuses Apply drawer from JobsPage
 *
 * @author Varun Pandey
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Briefcase,
  Calendar,
  MapPin,
  Clock,
  Users,
  ChevronRight,
  Star,
  FileText,
  Share2,
  AlertCircle,
  IndianRupee,
} from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../../api/client';
import { API_ENDPOINTS } from '../../api/endpoints';
import { useArtistDetailsStore } from '../../stores/artistDetailsStore';
import { useEventPricingStore } from '../../stores/eventPricingStore';
import { ApplyBidDrawer } from '../../components/features/ApplyBidDrawer';
import { Button } from '../../components/ui/Button';
import { Sheet } from '../../components/ui/Sheet';
import { Loader } from '@/components/ui';
import type { BookingFormItem } from '@/stores/bookingFormsStore';
import type { JobCardData } from '../../types/jobs';
import type { EventPricingApiItem } from '../../services/artistService';
import { EVENT_TYPES } from '../../constants/eventTypes';
import { cn } from '../../utils/cn';
import { toEventTypeDisplayLabel } from '../../utils/eventTypeDisplay';
import { toJobDescriptionDisplayText } from '../../utils/jobDescriptionDisplay';

interface JobDetailApiData {
  id: number;
  title: string;
  tbl: string;
  date: string;
  location: string;
  description: string;
  duration: number;
  audience: number;
  event_type: string;
  budget: string;
  is_applied: boolean;
  is_liked: boolean;
  pitched_artists: number;
  price: { original: number; discount: number; updated_price: number };
  status: boolean;
  is_recommended: boolean;
  deal_id: number;
  can_claim?: boolean;
  blocked_reason?: string | null;
  type?: 'Basic' | 'Recommended' | 'Express';
}

interface JobDetailApiResponse {
  success: boolean;
  data: JobDetailApiData;
}

/** Normalize job event_type to slug for matching (e.g. "Video Shoutout" -> "video-shoutout") */
function eventTypeToSlug(eventType: string): string {
  return eventType
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

/** Find event pricing for a job's event type. Match by event_name (case-insensitive) or e_slug. */
function getEventPricingForJob(
  eventPricingData: EventPricingApiItem[],
  jobEventType: string
): EventPricingApiItem | null {
  if (!jobEventType || !Array.isArray(eventPricingData)) return null;
  const normalized = jobEventType.trim().toLowerCase();
  const slug = eventTypeToSlug(jobEventType);
  for (const item of eventPricingData) {
    const name = (item.event?.event_name ?? '').trim().toLowerCase();
    const eslug = (item.event?.e_slug ?? '').trim().toLowerCase();
    if (name === normalized || eslug === slug || eslug === normalized) {
      return item;
    }
  }
  return null;
}

/** True if job can be applied to (event type exists in pricing with a base price). */
function canApplyToJob(eventPricingData: EventPricingApiItem[], jobEventType: string): boolean {
  const item = getEventPricingForJob(eventPricingData, jobEventType);
  if (!item) return false;
  const base = item.base_price;
  return typeof base === 'string' && base.trim() !== '' && Number(base) > 0;
}

function jobDetailToCardData(job: JobDetailApiData): JobCardData {
  const badge = job.is_recommended ? ('AI Recommended' as const) : undefined;
  return {
    id: String(job.id),
    category: job.event_type,
    amount: 0,
    budgetDisplay: job.budget,
    eventType: job.event_type,
    city: '',
    date: job.date,
    location: job.location,
    durationMinutes: job.duration,
    attendees: job.audience,
    eventTag: job.event_type,
    coinsToApply: job.price.updated_price,
    isSaved: job.is_liked,
    badge,
    title: job.title,
    description: job.description,
    type: job.type,
  };
}

export const JobDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: artistDetails } = useArtistDetailsStore();
  const eventPricingData = useEventPricingStore((s) => s.data);
  const ensureEventPricingFetched = useEventPricingStore((s) => s.ensureFetched);
  const createEventPricing = useEventPricingStore((s) => s.createEventPricing);
  const [bidDrawerOpen, setBidDrawerOpen] = useState(false);
  const [selectedJobForApply, setSelectedJobForApply] = useState<JobCardData | null>(null);
  const [addPricingSheetOpen, setAddPricingSheetOpen] = useState(false);
  const [addPricingEventSlug, setAddPricingEventSlug] = useState('');
  const [addPricingBasePrice, setAddPricingBasePrice] = useState('');
  const [addPricingSubmitting, setAddPricingSubmitting] = useState(false);

  useEffect(() => {
    void ensureEventPricingFetched();
  }, [ensureEventPricingFetched]);

  const showErrorToast = (message: string) => {
    toast.error(message);
  };

  const {
    data: job,
    isLoading: loading,
    error,
  } = useQuery<JobDetailApiData>({
    queryKey: ['jobDetail', id],
    enabled: !!id,
    staleTime: 60 * 1000,
    queryFn: async () => {
      if (!id) throw new Error('Invalid job ID');
      const res = await apiClient.get<JobDetailApiResponse>(API_ENDPOINTS.AUTH.JOBS_DETAIL(id));
      if (!res.data?.success || !res.data?.data) throw new Error('Failed to load job details');
      return res.data.data;
    },
  });

  const isExpressJob = (job?.type ?? '').trim().toLowerCase() === 'express';

  const openAddPricingForCurrentJob = () => {
    if (!job) return;
    const match = EVENT_TYPES.find(
      (e) =>
        e.event_name.toLowerCase() === (job.event_type ?? '').toLowerCase() ||
        e.e_slug === eventTypeToSlug(job.event_type ?? '')
    );
    setAddPricingEventSlug(match?.e_slug ?? EVENT_TYPES[0]?.e_slug ?? '');
    setAddPricingBasePrice('');
    setAddPricingSheetOpen(true);
  };

  const handleAddPricingSubmit = async () => {
    if (!job) return;
    const slug = addPricingEventSlug?.trim();
    const price = parseFloat(addPricingBasePrice);
    if (!slug || isNaN(price) || price < 0) {
      showErrorToast('Please select an event type and enter a valid base price.');
      return;
    }
    setAddPricingSubmitting(true);
    try {
      await createEventPricing({
        event_slug: slug,
        base_price: price.toFixed(2),
        festival_pricing: {},
      });
      toast.success('Event pricing saved.');
      setAddPricingSheetOpen(false);
      setAddPricingEventSlug('');
      setAddPricingBasePrice('');

      // After pricing is saved, apply normal profile-status gating and open pitch sheet if allowed.
      const status = (artistDetails?.user_info?.profile_status ?? '').trim().toLowerCase();
      const isLiveStatus = status === 'live' || status === 'approved';
      const isIncomplete = status === 'incomplete';
      const isOnReview = status === 'on review' || status === 'on_review' || status === 'pending';

      if (isIncomplete) {
        showErrorToast('Please complete your profile to start applying for jobs.');
        return;
      }
      if (isOnReview) {
        toast('Your profile is currently under review. Please wait for it to go live before applying for jobs.');
        return;
      }
      if (!isLiveStatus) {
        showErrorToast('Your profile is not live yet. Please wait for it to go live before applying for jobs.');
        return;
      }

      setSelectedJobForApply(jobDetailToCardData(job));
      setBidDrawerOpen(true);
    } catch {
      showErrorToast('Failed to save event pricing. Please try again.');
    } finally {
      setAddPricingSubmitting(false);
    }
  };

  const handleApply = async () => {
    if (!job) return;

    const jobEventType = job.event_type ?? '';
    const canApply = canApplyToJob(eventPricingData, jobEventType);

    const getExpressClaimBlockedMessage = (blockedReason?: string | null): string => {
      const reason = (blockedReason ?? '').trim().toLowerCase();
      if (!reason) return 'You cannot claim this express job right now.';
      switch (reason) {
        case 'same_date_conflict':
          return 'Only one express booking is allowed per day.';
        case 'already_claimed':
          return 'This booking has already been claimed by another artist.';
        case 'tier_mismatch':
          return 'This job requires a higher credibility tier.';
        case 'monthly_limit_reached':
          return 'Your monthly express booking limit has been reached.';
        default:
          return `You cannot claim this express job right now (${reason.replace(/_/g, ' ')}).`;
      }
    };

    const isFreeSubscription = (): boolean => {
      const subscriptionName = (artistDetails as { subscriptions?: { subscription_name?: string | null } } | null)
        ?.subscriptions?.subscription_name;
      const normalized = (subscriptionName ?? '').trim().toLowerCase();
      return normalized === '' || normalized === 'free';
    };

    // Express job claim flow
    if (isExpressJob) {
      if (job.can_claim === false) {
        const blockedReason = (job.blocked_reason ?? '').trim().toLowerCase();
        if (blockedReason === 'monthly_limit_reached' && isFreeSubscription()) {
          toast((t) => (
            <div className="flex flex-col gap-2">
              <p className="text-sm text-white">Your monthly express booking limit has been reached.</p>
              <button
                type="button"
                onClick={() => {
                  navigate('/membership');
                  toast.dismiss(t.id);
                }}
                className="w-fit rounded-lg bg-[#F33363] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#FF5E8E]"
              >
                Upgrade plan
              </button>
            </div>
          ), { duration: 8000 });
        } else {
          showErrorToast(getExpressClaimBlockedMessage(job.blocked_reason));
        }
        return;
      }

      const jobId = Number(job.id);
      if (!Number.isFinite(jobId)) {
        showErrorToast('Invalid job id.');
        return;
      }

      const tId = toast.loading('Opening booking form...');
      try {
        // Prefer deal_id from the fetched job; if missing, fetch job detail for deal_id
        let dealId = Number(job.deal_id);
        let detailData: JobDetailApiData | undefined = undefined;
        if (!Number.isFinite(dealId) || dealId === 0) {
          const res = await apiClient.get<JobDetailApiResponse>(API_ENDPOINTS.AUTH.JOBS_DETAIL(jobId));
          detailData = res.data?.success ? res.data.data : undefined;
          dealId = Number(detailData?.deal_id ?? 0);
        }

        if (!Number.isFinite(dealId) || dealId === 0) {
          showErrorToast('Booking info not found for this Express job.');
          return;
        }

        const d = detailData ?? job;
        const budgetStr = (d?.budget ?? '').trim();
        const feeDigits = budgetStr.replace(/[^\d]/g, '');
        const fee = feeDigits ? `₹${Number(feeDigits).toLocaleString('en-IN')}` : (budgetStr || '—');

        const syntheticForm: BookingFormItem = {
          id: Number(dealId),
          deal_id: Number(dealId),
          artist_id: 0,
          artist_type: '',
          artist_act: (artistDetails as any)?.artist_name ?? (artistDetails as any)?.user_info?.name ?? '—',
          contact_name: '',
          contact_phone: '',
          contact_email: '',
          full_address: '',
          city_state: '',
          members_count: 0,
          members_info: '[]',
          report_at: '',
          performance_duration_mins: Number(d?.duration ?? 0),
          tech_rider: '0',
          tbl_needed: 0,
          travel_boarding_lodging: '',
          event_type: String(d?.event_type ?? jobEventType ?? ''),
          venue: '',
          venue_city_state: String(d?.location ?? job.location ?? ''),
          bank_name: '',
          branch_name: '',
          ifsc_code: '',
          account_number: '',
          account_name: '',
          account_type: 'Savings',
          pan: '',
          gstin: null,
          fee: fee,
          advance_percentage: '',
          advance_amount: '',
          balance_amount: '',
          payment_terms: '',
          rating: 0,
          feedback: '',
          status: '',
          created_at: '',
          updated_at: '',
          tech_rider_value: '',
          gst_value: null,
          event_date: '',
          pin_code: null,
          venue_city: '',
          venue_state: '',
          artist_state: '',
          artist_city: '',
          google_map_link: null,
          event_start_at: null,
          taf: null,
          mode: null,
          description: String(d?.description ?? ''),
        };

        navigate(`/jobs/booking-fill/${dealId}`, { state: { form: syntheticForm } });
        return;
      } catch {
        showErrorToast('Failed to open booking form. Please try again.');
        return;
      } finally {
        toast.dismiss(tId);
      }
    }

    // Non-express flow (existing apply/bid)
    if (!canApply) {
      toast((t) => (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-white">
            Please add your pricing for {jobEventType || 'this event'} in Event Pricing to apply.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                toast.dismiss(t.id);
                openAddPricingForCurrentJob();
              }}
              className="rounded-lg bg-[#F33363] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#FF5E8E]"
            >
              Add pricing
            </button>
            <button
              type="button"
              onClick={() => toast.dismiss(t.id)}
              className="rounded-lg border border-[#3A3A3C] px-3 py-1.5 text-xs font-medium text-[#8E8E93] hover:bg-[#2C2C2E]"
            >
              Not now
            </button>
          </div>
        </div>
      ), { duration: 8000 });
      return;
    }

    const status = (artistDetails?.user_info?.profile_status ?? '').trim().toLowerCase();
    const isLiveStatus = status === 'live' || status === 'approved';
    const isIncomplete = status === 'incomplete';
    const isOnReview = status === 'on review' || status === 'on_review' || status === 'pending';

    if (isIncomplete) {
      showErrorToast('Please complete your profile to start applying for jobs.');
      return;
    }
    if (isOnReview) {
      toast('Your profile is currently under review. Please wait for it to go live before applying for jobs.');
      return;
    }
    if (!isLiveStatus) {
      showErrorToast('Your profile is not live yet. Please wait for it to go live before applying for jobs.');
      return;
    }

    setSelectedJobForApply(jobDetailToCardData(job));
    setBidDrawerOpen(true);
  };

  const handleConfirmBid = () => {
    setSelectedJobForApply(null);
  };

  const handleShare = () => {
    if (!job) return;
    if (navigator.share) {
      navigator.share({
        title: job.title,
        text: job.description,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard');
    }
  };

  if (loading) {
    return (
      <div className="w-full min-h-[60vh] flex items-center justify-center">
        <Loader size="small" />
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="w-full min-h-[60vh] flex flex-col items-center justify-center gap-4 px-6">
        <p className="text-[#8E8E93] text-center">{(error as Error | null)?.message ?? 'Job not found'}</p>
        <button
          onClick={() => navigate('/jobs')}
          className="px-6 py-3 rounded-xl border border-[#3A3A3C] bg-[#2C2C2E] text-white hover:border-[#F33363]/50 transition-colors font-medium"
        >
          Back to Jobs
        </button>
      </div>
    );
  }

  const canApply = canApplyToJob(eventPricingData, job.event_type ?? '');
  const canTakeAction = isExpressJob ? job.can_claim !== false : canApply;

  return (
    <div className="w-full min-h-screen bg-[#0B0B0D] text-white">
      <div className="w-full md:px-4 py-10 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className=" mx-auto space-y-6"
        >
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <Briefcase className="h-8 w-8 lg:h-10 lg:w-10 text-[#8E8E93]" />
              <h1 className="text-2xl lg:text-3xl font-bold text-white">Job Details</h1>
            </div>
          </div>

          {/* Main card */}
          <div className="relative overflow-hidden rounded-2xl bg-[#1C1C1E] border border-[#3A3A3C]/50">
            <div className="absolute inset-0 bg-gradient-to-br from-[#F33363]/5 to-transparent pointer-events-none" />
            <div className="relative p-6 lg:p-8 space-y-6">
              {/* Badge */}
              {job.is_recommended && (
                <span className="inline-block px-3 py-1 rounded-full bg-[#F33363]/20 border border-[#F33363]/40 text-[#F33363] text-xs font-bold uppercase tracking-wider">
                  AI Recommended
                </span>
              )}

              {/* Title */}
              <h2 className="text-xl lg:text-2xl font-bold text-white leading-tight">
                {job.title}
              </h2>

              {!isExpressJob && !canApply && (
                <div className="flex items-center gap-2 rounded-lg bg-amber-500/10 border border-amber-500/20 px-3 py-2">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 text-amber-500" />
                  <p className="text-xs text-amber-200">
                    Add your pricing for <span className="font-semibold text-white">{toEventTypeDisplayLabel(job.event_type)}</span> in Event Pricing to apply.
                  </p>
                  <button
                    type="button"
                    onClick={openAddPricingForCurrentJob}
                    className="ml-auto flex-shrink-0 rounded-lg bg-amber-500/20 px-2.5 py-1 text-[10px] font-bold text-amber-300 hover:bg-amber-500/30"
                  >
                    Add pricing
                  </button>
                </div>
              )}

              {/* Meta row */}
              <div className="flex flex-wrap items-center gap-4 text-sm text-[#8E8E93]">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-[#F33363] flex-shrink-0" />
                  <span>{job.location}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-[#8E8E93] flex-shrink-0" />
                  <span>{job.date}</span>
                </div>
              </div>

              {/* Info grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="p-4 rounded-xl bg-[#2C2C2E]/50 border border-[#3A3A3C]/50">
                  <div className="flex items-center gap-2 text-[#8E8E93] text-xs font-bold uppercase tracking-wider mb-1">
                    <Clock className="h-3.5 w-3.5" />
                    Duration
                  </div>
                  <p className="text-white font-semibold">{job.duration} min</p>
                </div>
                <div className="p-4 rounded-xl bg-[#2C2C2E]/50 border border-[#3A3A3C]/50">
                  <div className="flex items-center gap-2 text-[#8E8E93] text-xs font-bold uppercase tracking-wider mb-1">
                    <Users className="h-3.5 w-3.5" />
                    Audience
                  </div>
                  <p className="text-white font-semibold">{job.audience} PAX</p>
                </div>
                <div className="p-4 rounded-xl bg-[#2C2C2E]/50 border border-[#3A3A3C]/50">
                  <div className="flex items-center gap-2 text-[#8E8E93] text-xs font-bold uppercase tracking-wider mb-1">
                    <FileText className="h-3.5 w-3.5" />
                    Event Type
                  </div>
                  <p className="text-white font-semibold">{toEventTypeDisplayLabel(job.event_type)}</p>
                </div>
                <div className="p-4 rounded-xl bg-[#2C2C2E]/50 border border-[#3A3A3C]/50">
                  <div className="flex items-center gap-2 text-[#8E8E93] text-xs font-bold uppercase tracking-wider mb-1">
                    <IndianRupee className="h-3.5 w-3.5" />
                    Budget
                  </div>
                  <p className="text-white font-semibold">{job.budget}</p>
                </div>
              </div>

              {/* Description */}
              <div>
                <h3 className="text-sm font-bold text-[#8E8E93] uppercase tracking-wider mb-2">
                  Description
                </h3>
                <p className="text-white text-sm leading-relaxed">{toJobDescriptionDisplayText(job.description)}</p>
              </div>

              {/* Price info */}
              {!isExpressJob && (
                <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-[#3A3A3C]/50">
                  <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#F33363]/10 border border-[#F33363]/20">
                    <Star className="h-4 w-4 text-[#F33363] fill-[#F33363]" />
                    <span className="text-sm font-bold text-[#F33363]">
                      {job.price.updated_price} StarCoins to apply
                    </span>
                  </div>
                  {job.pitched_artists > 0 && (
                    <p className="text-xs text-[#8E8E93]">
                      {job.pitched_artists} artist{job.pitched_artists !== 1 ? 's' : ''} pitched
                    </p>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap items-center gap-3 pt-4">
                <Button
                  variant="active"
                  onClick={handleApply}
                  disabled={job.is_applied}
                  aria-disabled={job.is_applied || !canTakeAction}
                  className={cn(
                    "flex items-center gap-2 px-6 py-3 font-bold transition-all",
                    job.is_applied
                      ? "bg-[#2C2C2E] text-[#8E8E93]"
                      : canTakeAction
                        ? "bg-[#F33363] text-white hover:bg-[#FF5E8E] active:scale-95"
                        : "bg-[#F33363]/15 text-[#F33363] border border-[#F33363]/40 hover:bg-[#F33363]/20 hover:border-[#F33363]/60"
                  )}
                >
                  {job.is_applied ? 'Already Applied' : (isExpressJob ? 'Claim' : 'Apply')}
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <button
                  onClick={handleShare}
                  className="flex items-center gap-2 px-4 py-3 rounded-xl border border-[#3A3A3C] bg-[#2C2C2E] text-white hover:border-[#F33363]/50 transition-colors"
                >
                  <Share2 className="h-4 w-4 text-[#8E8E93]" />
                  Share
                </button>
              </div>
            </div>
          </div>


        </motion.div>
      </div>

      <ApplyBidDrawer
        open={bidDrawerOpen}
        onOpenChange={(open) => {
          setBidDrawerOpen(open);
          if (!open) setSelectedJobForApply(null);
        }}
        job={selectedJobForApply}
        onConfirm={handleConfirmBid}
      />

      {/* Add Event Pricing sheet — add pricing for job's event type without leaving Job Detail */}
      <Sheet
        open={addPricingSheetOpen}
        onOpenChange={(open) => {
          if (!open) {
            setAddPricingSheetOpen(false);
            setAddPricingEventSlug('');
            setAddPricingBasePrice('');
          }
        }}
      >
        {job && (
          <div className="space-y-5 pt-2 w-full">
            <div className="flex items-center gap-2">
              <IndianRupee className="h-5 w-5 text-[#F33363]" />
              <h2 className="text-xl font-bold text-white">Add event pricing</h2>
            </div>
            <p className="text-sm text-[#8E8E93]">
              Add your pricing for <span className="font-semibold text-white">{toEventTypeDisplayLabel(job.event_type)}</span> so you can apply to this job.
            </p>
            <div className="space-y-2">
              <label className="block text-xs font-semibold text-[#8E8E93]">Event type</label>
              <select
                value={addPricingEventSlug}
                onChange={(e) => setAddPricingEventSlug(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-[#3A3A3C] bg-transparent text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#F33363]"
              >
                {EVENT_TYPES.map((e) => (
                  <option key={e.e_slug} value={e.e_slug}>
                    {toEventTypeDisplayLabel(e.event_name)}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <label className="block text-xs font-semibold text-[#8E8E93]">Base price (₹)</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8E8E93] text-lg">₹</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={addPricingBasePrice}
                  onChange={(e) => setAddPricingBasePrice(e.target.value.replace(/\D/g, ''))}
                  placeholder="0"
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-[#3A3A3C] bg-transparent text-white placeholder-[#8E8E93] focus:outline-none focus:ring-2 focus:ring-[#F33363]"
                />
              </div>
            </div>
            <Button
              variant="active"
              onClick={() => void handleAddPricingSubmit()}
              disabled={addPricingSubmitting || !addPricingBasePrice.trim()}
              isLoading={addPricingSubmitting}
              className="w-full py-3.5 font-medium"
            >
              Save & apply to job
            </Button>
          </div>
        )}
      </Sheet>
    </div>
  );
};
